import React, { useState, useEffect } from 'react';
import { rolesAPI } from '../../services/api';
import Modal from '../../components/common/Modal';
import PermissionGuard from '../../components/common/PermissionGuard';
import Spinner from '../../components/common/Spinner';
import toast from 'react-hot-toast';

const MODULES = ['users', 'customers', 'plans', 'resellers', 'subscriptions', 'payments', 'roles', 'reports', 'settings'];
const ACTIONS = ['create', 'read', 'update', 'delete'];

const defaultPermissions = MODULES.map(m => ({
  module: m,
  actions: { create: false, read: false, update: false, delete: false }
}));

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', permissions: defaultPermissions });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    rolesAPI.getAll()
      .then(res => setRoles(res.data.data))
      .catch(() => toast.error('Failed to load roles'))
      .finally(() => setLoading(false));
  }, []);

  const openCreate = () => {
    setEditRole(null);
    setForm({ name: '', description: '', permissions: defaultPermissions.map(p => ({ ...p, actions: { ...p.actions } })) });
    setShowModal(true);
  };

  const openEdit = (role) => {
    setEditRole(role);
    const permissions = MODULES.map(m => {
      const existing = role.permissions?.find(p => p.module === m);
      return { module: m, actions: existing?.actions || { create: false, read: false, update: false, delete: false } };
    });
    setForm({ name: role.name, description: role.description || '', permissions });
    setShowModal(true);
  };

  const toggleAction = (moduleIdx, action) => {
    const perms = [...form.permissions];
    perms[moduleIdx] = { ...perms[moduleIdx], actions: { ...perms[moduleIdx].actions, [action]: !perms[moduleIdx].actions[action] } };
    setForm({ ...form, permissions: perms });
  };

  const toggleAll = (moduleIdx) => {
    const perms = [...form.permissions];
    const allTrue = ACTIONS.every(a => perms[moduleIdx].actions[a]);
    perms[moduleIdx] = { ...perms[moduleIdx], actions: Object.fromEntries(ACTIONS.map(a => [a, !allTrue])) };
    setForm({ ...form, permissions: perms });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editRole) {
        await rolesAPI.update(editRole._id, form);
        toast.success('Role updated');
      } else {
        await rolesAPI.create(form);
        toast.success('Role created');
      }
      setShowModal(false);
      const res = await rolesAPI.getAll();
      setRoles(res.data.data);
    } catch (err) { toast.error(err.response?.data?.message || 'Operation failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this role?')) return;
    try {
      await rolesAPI.delete(id);
      toast.success('Role deleted');
      setRoles(roles.filter(r => r._id !== id));
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <PermissionGuard module="roles" action="create">
          <button onClick={openCreate} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition">
            + Create Role
          </button>
        </PermissionGuard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {roles.map(role => (
          <div key={role._id} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">{role.name}</h3>
                {role.description && <p className="text-xs text-gray-500 mt-1">{role.description}</p>}
                {role.isSystem && <span className="inline-block mt-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">System</span>}
              </div>
              {!role.isSystem && (
                <div className="flex gap-2">
                  <PermissionGuard module="roles" action="update">
                    <button onClick={() => openEdit(role)} className="text-indigo-600 text-xs hover:underline">Edit</button>
                  </PermissionGuard>
                  <PermissionGuard module="roles" action="delete">
                    <button onClick={() => handleDelete(role._id)} className="text-red-600 text-xs hover:underline">Delete</button>
                  </PermissionGuard>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              {role.permissions?.slice(0, 4).map(perm => (
                <div key={perm.module} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 capitalize">{perm.module}</span>
                  <div className="flex gap-1">
                    {ACTIONS.map(a => (
                      <span key={a} className={`px-1.5 py-0.5 rounded text-xs ${perm.actions[a] ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                        {a.charAt(0).toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {(role.permissions?.length || 0) > 4 && (
                <p className="text-xs text-gray-400">+{role.permissions.length - 4} more modules</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editRole ? 'Edit Role' : 'Create Role'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Module</th>
                    {ACTIONS.map(a => <th key={a} className="px-3 py-2 text-center font-medium text-gray-600 capitalize">{a}</th>)}
                    <th className="px-3 py-2 text-center font-medium text-gray-600">All</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {form.permissions.map((perm, idx) => (
                    <tr key={perm.module} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium capitalize">{perm.module}</td>
                      {ACTIONS.map(a => (
                        <td key={a} className="px-3 py-2.5 text-center">
                          <input type="checkbox" checked={perm.actions[a]}
                            onChange={() => toggleAction(idx, a)}
                            className="w-4 h-4 text-indigo-600 rounded cursor-pointer" />
                        </td>
                      ))}
                      <td className="px-3 py-2.5 text-center">
                        <input type="checkbox" checked={ACTIONS.every(a => perm.actions[a])}
                          onChange={() => toggleAll(idx)}
                          className="w-4 h-4 text-indigo-600 rounded cursor-pointer" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
              {saving ? 'Saving...' : (editRole ? 'Update Role' : 'Create Role')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Roles;
